/**
 * MainView — Renders the breakdown modal for a single KPI. SE-5024.
 *
 * Acts as a tiny dispatcher: GPR / Player Style Fit / Team Style Fit go to
 * MetricExplanationModal; Gemini Player Valuation (transferValue) goes to
 * TransferValueModal. Both modals own their own backdrop + close affordance;
 * `onClose` here dismisses the route — `MainContainer.handleClose` pops it when
 * there is a route to pop and replaces into the player page when there is not,
 * which is what makes Close work on a cold-loaded breakdown (SE-8303).
 *
 * The route mounts its own `PortalHost` named PLAYER_DETAIL_PORTAL so the
 * modal's web branch — which renders via GorhomPortal into that host — has
 * a destination when the breakdown is reached as a standalone route rather
 * than as an in-place modal inside the player page.
 *
 * `hostIsModalRoute` tells the explanation modal that this screen IS the
 * presentation (the route is a native `transparentModal`), so on native it
 * renders in place rather than presenting a second RN Modal mid-transition —
 * the SE-8770 first-open header-under-the-status-bar defect.
 */

import { memo } from "react";

import MetricExplanationModal from "@/components/organisms/MetricExplanationModal";
import TransferValueModal from "@/components/organisms/TransferValueModal";
import { Box } from "@/components/atoms/Box";
import { PortalHost, PLAYER_DETAIL_PORTAL } from "@/components/atoms/Portal";
import {
  CurrencyCode,
  PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";

import type { BreakdownMetric } from "../../constants";

/**
 * Props for the breakdown route's MainView dispatcher.
 */
interface MainViewProps {
  readonly metric: BreakdownMetric;
  readonly metricId: string | null;
  readonly metricValue: number | null;
  readonly rosterValues: number[];
  readonly player: PlayerDetailPlayerMetaFragment | null;
  readonly listPlayerSameTeamAndPosition?: PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery;
  readonly isFetchingListPlayerSameTeamAndPosition: boolean;
  readonly currencyCode: CurrencyCode;
  readonly onClose: () => void;
  /** True when mounted as the breakdown route, which is itself a native modal (SE-8770). */
  readonly hostIsModalRoute: boolean;
}

/**
 * Renders the appropriate breakdown modal for the active metric. Returns null
 * while the player is still loading on a transferValue route, since
 * TransferValueModal requires a non-null player.
 * @param root0 - View props
 * @param root0.metric - The breakdown metric type the route is mounting
 * @param root0.metricId - MetricExplanationModal explanation id, null for transferValue
 * @param root0.metricValue - Raw numeric value for the metric, null for transferValue
 * @param root0.rosterValues - Roster comparison values for MetricExplanationModal
 * @param root0.player - Player fragment for the current route playerId
 * @param root0.listPlayerSameTeamAndPosition - Roster query result for distribution
 * @param root0.isFetchingListPlayerSameTeamAndPosition - Loading state for roster query
 * @param root0.currencyCode - Currency for TransferValueModal display
 * @param root0.onClose - Callback that dismisses the route — pops it, or replaces
 * into the player page when there is nothing to pop (SE-8303)
 * @param root0.hostIsModalRoute - Mounted as the breakdown route, itself a native modal (SE-8770)
 */
const MainView = ({
  metric,
  metricId,
  metricValue,
  rosterValues,
  player,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  currencyCode,
  onClose,
  hostIsModalRoute,
}: MainViewProps) => (
  <Box className="flex-1">
    <PortalHost name={PLAYER_DETAIL_PORTAL} />
    {metric === "transferValue" ? (
      player && (
        <TransferValueModal
          isOpen
          onClose={onClose}
          player={player}
          currencyCode={currencyCode}
        />
      )
    ) : (
      <MetricExplanationModal
        isOpen
        onClose={onClose}
        hostIsModalRoute={hostIsModalRoute}
        metricId={metricId}
        metricValue={metricValue}
        rosterValues={rosterValues}
        player={player ?? undefined}
        listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
        isFetchingListPlayerSameTeamAndPosition={
          isFetchingListPlayerSameTeamAndPosition
        }
      />
    )}
  </Box>
);

MainView.displayName = "PlayerMetricBreakdownMainView";

export default memo(MainView);
